import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    createFilterOptions,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Account from '../helpers/Account';
import Api from '../Api';
import UrsHeader from '../components/UrsHeader';

const { REACT_APP_SERVER } = process.env;
const accent = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const accentHover = REACT_APP_SERVER === 'ATS' ? '#095a8f' : '#6aa507';

const filter = createFilterOptions();

const generatePromoCode = () => Math.random().toString(36).slice(2, 10).toUpperCase();

const formatCreatorLabel = (user) => {
    if (!user) return '';
    const name = user.fio || user.name || user.phone || '';
    return user.id ? `${user.id} - ${name}` : name;
};

const resolveCreatorPayload = (value, users) => {
    if (!value) return null;
    if (typeof value === 'string') {
        const name = value.trim();
        return name ? { creator_name: name } : null;
    }
    if (value.inputValue) {
        const name = String(value.inputValue).trim();
        return name ? { creator_name: name } : null;
    }
    if (value.id) {
        const u = value.user || users.find((item) => String(item.id) === String(value.id));
        const name = String(u?.fio || u?.name || u?.phone || '').trim();
        return {
            member_id: value.id,
            ...(name ? { creator_name: name } : {}),
        };
    }
    const name = String(value.label || '').trim();
    return name ? { creator_name: name } : null;
};

const formatCreatorDisplay = (row) => {
    if (row?.creator_name) return row.creator_name;
    if (row?.member_id) {
        return `${row.member_id} - ${row.member_fio || row.member_name || row.member_phone || ''}`.trim();
    }
    return '-';
};

export default function UrsPromocodes() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [used, setUsed] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(0);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [promoCode, setPromoCode] = useState(generatePromoCode());
    const [days, setDays] = useState(30);

    const creatorOptions = useMemo(
        () => users.map((user) => ({
            id: user.id,
            label: formatCreatorLabel(user),
            user,
        })),
        [users]
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: usersData }, { data: promoData }] = await Promise.all([
                Api.ursPromocodesUsers(),
                Api.ursPromocodes(),
            ]);
            setUsers(usersData?.users || []);
            setRows(promoData?.rows || []);
            setUsed(promoData?.used || []);
        } catch (e) {
            console.log(e);
            toast.error('Failed to load promocodes');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!Account.getUrsToken()) {
            navigate('/admin_login');
            return;
        }
        loadData();
    }, [navigate, loadData]);

    const usedRows = useMemo(() => used || [], [used]);

    const handleSave = useCallback(async () => {
        const creatorPayload = resolveCreatorPayload(selectedCreator, users);
        if (!creatorPayload?.member_id && !creatorPayload?.creator_name) {
            toast.error('Please enter or select creator');
            return;
        }
        const preparedCode = String(promoCode || '').trim().toUpperCase();
        if (!preparedCode) {
            toast.error('Please add promocode');
            return;
        }
        const daysNum = Number(days);
        if (!daysNum || daysNum < 1) {
            toast.error('Days must be greater than 0');
            return;
        }

        setSaving(true);
        try {
            await Api.ursPromocodesCreate({
                ...creatorPayload,
                promo_code: preparedCode,
                days: daysNum,
            });
            toast.success('Promocode saved');
            setPromoCode(generatePromoCode());
            setSelectedCreator(null);
            await loadData();
        } catch (e) {
            console.log(e);
            toast.error(e?.response?.data?.message || 'Failed to save promocode');
        } finally {
            setSaving(false);
        }
    }, [selectedCreator, users, promoCode, days, loadData]);

    const handleDelete = useCallback(async (id) => {
        const text = 'Are you sure you want to delete this promocode?';
        if (!window.confirm(text)) return;
        try {
            setDeletingId(id);
            await Api.ursPromocodesDelete(id);
            toast.success('Promocode deleted');
            await loadData();
        } catch (e) {
            console.log(e);
            toast.error(e?.response?.data?.message || 'Failed to delete promocode');
        } finally {
            setDeletingId(0);
        }
    }, [loadData]);

    return (
        <>
            <Helmet>
                <title>Urs Promocodes</title>
            </Helmet>
            <UrsHeader />
            <div className="urs_table" style={{ display: 'flex', justifyContent: 'center', paddingTop: 20 }}>
                <div className="tableParent file" style={{ background: '#fff', padding: 24, width: 'min(1200px, 96vw)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Promocodes</Typography>
                        <Link to="/urs" style={{ color: accent }}>Back to users</Link>
                    </Box>

                    <Paper sx={{ p: 2, border: '1px solid #e5e7eb', mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Create promocode</Typography>
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr auto' }, alignItems: 'center' }}>
                            <Autocomplete
                                freeSolo
                                value={selectedCreator}
                                onChange={(_, newValue) => {
                                    if (typeof newValue === 'string') {
                                        setSelectedCreator({ inputValue: newValue, label: newValue });
                                        return;
                                    }
                                    if (newValue?.inputValue) {
                                        setSelectedCreator({ inputValue: newValue.inputValue, label: newValue.inputValue });
                                        return;
                                    }
                                    setSelectedCreator(newValue);
                                }}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const { inputValue } = params;
                                    const isExisting = options.some(
                                        (option) => inputValue.toLowerCase() === option.label.toLowerCase()
                                    );
                                    if (inputValue !== '' && !isExisting) {
                                        filtered.push({
                                            inputValue,
                                            label: `Add "${inputValue}"`,
                                        });
                                    }
                                    return filtered;
                                }}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                options={creatorOptions}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    if (option.inputValue) return option.inputValue;
                                    return option.label || '';
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false;
                                    if (option.inputValue && value.inputValue) {
                                        return option.inputValue === value.inputValue;
                                    }
                                    return String(option.id) === String(value.id);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id || option.inputValue || option.label}>
                                        {option.label}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Creator name" size="small" placeholder="Select or type name" />
                                )}
                            />
                            <TextField
                                label="Promocode"
                                size="small"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            />
                            <TextField
                                label="Days"
                                size="small"
                                type="number"
                                inputProps={{ min: 1, max: 3650 }}
                                value={days}
                                onChange={(e) => setDays(Math.max(1, Number(e.target.value || 1)))}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    sx={{ textTransform: 'none', color: accent, borderColor: accent, '&:hover': { borderColor: accentHover, color: accentHover } }}
                                    onClick={() => setPromoCode(generatePromoCode())}
                                >
                                    Generate
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{ textTransform: 'none', backgroundColor: accent, '&:hover': { backgroundColor: accentHover } }}
                                    disabled={saving}
                                    onClick={handleSave}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper sx={{ p: 2, border: '1px solid #e5e7eb', mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Created promocodes ({rows.length})</Typography>
                        {loading ? (
                            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Creator</TableCell>
                                        <TableCell>Promocode</TableCell>
                                        <TableCell>Days</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Expires</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>{formatCreatorDisplay(row)}</TableCell>
                                            <TableCell>{row.promo_code}</TableCell>
                                            <TableCell>{row.days}</TableCell>
                                            <TableCell>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: 12,
                                                    color: '#fff',
                                                    background: Number(row.is_active) === 1 ? '#16a34a' : '#ce405f'
                                                }}>
                                                    {Number(row.is_active) === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </TableCell>
                                            <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                                            <TableCell>{row.expires_at ? new Date(row.expires_at).toLocaleString() : '-'}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    disabled={deletingId === row.id}
                                                    onClick={() => handleDelete(row.id)}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {deletingId === row.id ? '...' : 'Delete'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!rows.length ? (
                                        <TableRow><TableCell colSpan={8} align="center">No promocodes yet</TableCell></TableRow>
                                    ) : null}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2, border: '1px solid #e5e7eb' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Users who used promocode ({usedRows.length})</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Promocode</TableCell>
                                    <TableCell>Creator</TableCell>
                                    <TableCell>Used by</TableCell>
                                    <TableCell>Used at</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usedRows.map((row) => (
                                    <TableRow key={`used-${row.id}`}>
                                        <TableCell>{row.promo_code}</TableCell>
                                        <TableCell>{formatCreatorDisplay(row)}</TableCell>
                                        <TableCell>{row.used_by} - {row.used_fio || row.used_name || row.used_phone || ''}</TableCell>
                                        <TableCell>{row.used_at ? new Date(row.used_at).toLocaleString() : '-'}</TableCell>
                                    </TableRow>
                                ))}
                                {!usedRows.length ? (
                                    <TableRow><TableCell colSpan={4} align="center">No used promocodes yet</TableCell></TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </Paper>
                </div>
            </div>
        </>
    );
}
